import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import supabase from "../utils/supabase";
import useUserStore from "./stores/userStore";
import LoadingState from "./components/LoadingState";
import { useAnalyticsLifecycle, useAnalyticsActions, useAnalyticsStore } from "./hooks/useAnalytics";
import DashboardLoadingShell from "./components/DashboardLoadingShell";

const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const Callback = lazy(() => import("./pages/auth/Callback"));
const Unauthorized = lazy(() => import("./components/Unauthorized"));
const NotFound = lazy(() => import("./components/NotFound"));
const UnitsPage = lazy(() => import("./pages/UnitsPage"));
const Layout = lazy(() => import("./pages/Layout"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const LessonsPage = lazy(() => import("./pages/LessonsPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const LandingPage = lazy(() => import("./pages/landing/LandingPage"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ManageContentPage = lazy(() => import("./pages/ManageContentPage"));
const ContentAdminRoute = lazy(() => import("./components/ContentAdminRoute"));

function FullScreenLoader() {
  if (window.location.pathname.startsWith("/dashboard")) {
    return <DashboardLoadingShell />;
  }

  return <LoadingState label="Loading Faheem" fullPage />;
}

/**
 * AnalyticsSessionManager
 * Manages the app-level analytics session lifecycle.
 * Uses useAnalyticsLifecycle for singleton timers/listeners (heartbeat, idle detection, etc.)
 * Uses useAnalyticsActions to start session when user authenticates.
 * Renders nothing - this is a side-effect only component.
 */
function AnalyticsSessionManager() {
  const user = useUserStore((state) => state.user);
  const appSessionId = useAnalyticsStore((state) => state.appSessionId);
  const isHydrated = useAnalyticsStore((state) => state.isHydrated);
  const isSigningOut = useAnalyticsStore((state) => state.isSigningOut);

  useEffect(() => {
    if (!user?.id) return;
    useAnalyticsStore.getState().setHydrated(false);
    void useAnalyticsStore.persist.rehydrate();
  }, [user?.id]);

  // Lifecycle hook manages heartbeats, idle detection, and page visibility.
  // MUST only be called once in the app (singleton pattern)
  useAnalyticsLifecycle(user?.id);

  // Actions hook - for starting/ending sessions
  const { startAppSession } = useAnalyticsActions(user?.id);

  useEffect(() => {
    if (!user?.id || appSessionId || !isHydrated || isSigningOut) return;

    let cancelled = false;
    let retryTimer: number | null = null;

    const ensureSession = async (attempt: number) => {
      const sessionId = await startAppSession();
      if (sessionId || cancelled) return;

      const delay = Math.min(30000, 1000 * 2 ** attempt);
      retryTimer = window.setTimeout(() => {
        void ensureSession(attempt + 1);
      }, delay);
    };

    void ensureSession(0);

    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [user?.id, appSessionId, isHydrated, isSigningOut, startAppSession]);

  return null;
}

function RootRedirect() {
  const user = useUserStore((state) => state.user);
  const isAuthReady = useUserStore((state) => state.isAuthReady);

  if (!isAuthReady) {
    return <FullScreenLoader />;
  }

  if (user) {
    return <Navigate to="/dashboard/courses" replace />;
  }

  return <Navigate to="/home" replace />;
}

function App() {
  const { pathname } = useLocation();
  const initializeUser = useUserStore((state) => state.initializeUser);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await initializeUser();
      } catch (error) {
        console.error("Error initializing user:", error);
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    };

    initialize();
    return () => {
      cancelled = true;
    };
  }, [initializeUser]);

  if (isInitializing) {
    return <FullScreenLoader />;
  }

  return (
    <Suspense fallback={<FullScreenLoader />}>
      {pathname !== "/forgot-password" && pathname !== "/forgot-password/" && <AnalyticsSessionManager />}
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="home" element={<LandingPage />} />
        <Route path="signin" element={<SignIn supabase={supabase} />} />
        <Route path="signup" element={<SignUp supabase={supabase} />} />
        <Route
          path="settings"
          element={<Navigate to="/dashboard/profile" replace />}
        />
        <Route
          path="auth/callback"
          element={<Callback supabase={supabase} />}
        />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="unauthorized" element={<Unauthorized />} />
        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="courses" replace />} />
          <Route path="profile" element={<SettingsPage />} />
          <Route path="courses" element={<CoursesPage />} />
          <Route path="units" element={<UnitsPage />} />
          <Route path="learn" element={<LessonsPage />} />
          <Route
            path="manage-content"
            element={
              <ContentAdminRoute>
                <ManageContentPage />
              </ContentAdminRoute>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
